/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import Fuse from 'fuse.js';
import { BlocksIcon, ExternalLinkIcon, ListIcon } from 'lucide-react';
import { useEffect, useState, ChangeEvent, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import { IWithPagination, SuiteExecutionTrigger } from '../../commons/commons';
import { useUserContext } from '../Root';

import StoriesSummary from './StoriesSummary';

import { RunExecutionDetails, Story } from '@/commons/protocols';
import { Button as NewButton } from '@/components/atoms/Button';
import DisplayError, { formatError } from '@/components/atoms/Error';
import SearchBar from '@/components/atoms/SearchBar';
import AddToCiModal from '@/components/suite/AddToCiModal';
import StoryCard from '@/components/suite/StoryCard';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useGet, useGetInfinite, useMutate } from '@/net/commons';

export const executionSchema = z.object({
    execution: z.object({
        id: z.number(),
        createdAt: z.string(),
        testSuiteId: z.number(),
        parentId: z.number().nullable(),
        runs: z.array(z.string()),
    }),
});

type SuiteDashboardProps = {
    block?: boolean;
};

const SuiteDashboard = ({ block = false }: SuiteDashboardProps) => {
    const { projectSlug, suiteSlug } = useParams();

    const [filteredData, setFilteredData] = useState<Story[]>([]);
    const [fuse, setFuse] = useState<Fuse<Story> | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<any>(undefined);
    const [singleStorySubmittingId, setSingleStorySubmittingId] = useState<number | undefined>(undefined);
    const [selectedStoryCards, setSelectedStoryCard] = useState<number[]>([]);
    const [viewArchive] = useState<boolean>(false);
    const [highlightedStory, setHighlightedStory] = useState<number | undefined>(undefined);
    const [isCiModalOpen, setIsCiModalOpen] = useState(false);
    const { canOperate, hasSuperUserPermissions } = useUserContext().user;

    const navigate = useNavigate();

    const { toast } = useToast();

    const fuseOptions = {
        keys: ['name'],
    };

    const {
        data: storiesData,
        isLoading: isStoriesLoading,
        error: errorStories,
        mutate,
    } = useGet<Story[]>(
        block
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/blocks`
            : `/bff/project/${projectSlug}/suite/${suiteSlug}/stories`,
    );

    const nonArchivedStories = storiesData?.filter((story) => !story.archived) || [];
    const lastRPressRef = useRef<number>(0);

    const handleCmd = useCallback(
        (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
                // Only act if there are stories and not all are selected
                if (filteredData.length > 0 && selectedStoryCards.length !== filteredData.length) {
                    e.preventDefault();
                    setSelectedStoryCard(filteredData.map((story) => story.id));
                }
            }
            if (e.key === 'Escape') {
                setSelectedStoryCard([]);
            }
            if (e.key === 'r' || e.key === 'R') {
                const now = Date.now();
                if (now - lastRPressRef.current < 300) {
                    // Double press detected
                    handleRun();
                }
                lastRPressRef.current = now;
            }
        },
        [filteredData, selectedStoryCards.length],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleCmd);
        return () => window.removeEventListener('keydown', handleCmd);
    }, [handleCmd]);

    // Add story status counts
    const numStoriesOn = nonArchivedStories.filter((s) => s.enabled && !s.maintenance && !s.draft).length;
    const numStoriesMaintenance = nonArchivedStories.filter((s) => s.maintenance).length;
    const numStoriesDraft = nonArchivedStories.filter((s) => s.draft).length;
    const numStoriesDisabled = nonArchivedStories.filter((s) => !s.enabled).length;

    useEffect(() => {
        if (!viewArchive && nonArchivedStories) {
            setFilteredData(nonArchivedStories);
            setFuse(new Fuse(nonArchivedStories, fuseOptions));
        } else {
            setFilteredData(storiesData || []);
            setFuse(new Fuse(storiesData || [], fuseOptions));
        }
    }, [storiesData, isStoriesLoading, viewArchive]);

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        if (event?.target?.value === '') {
            nonArchivedStories && setFilteredData(nonArchivedStories);
        } else if (fuse) {
            const searchResults = fuse.search(event?.target?.value);
            searchResults && setFilteredData(searchResults.map((e) => e.item));
        }
    };

    const { trigger } = useMutate<SuiteExecutionTrigger>(`/bff/project/${projectSlug}/suite/${suiteSlug}/trigger`, {
        revalidate: true,
        method: 'POST',
    });

    const getKey = () => {
        return `/bff/project/${projectSlug}/suite/${suiteSlug}/executions`;
    };

    const { mutate: refreshExecutions } = useGetInfinite<IWithPagination<RunExecutionDetails>>(getKey);

    const [searchParams] = useSearchParams();
    const h = searchParams.get('highlightStory');

    useEffect(() => {
        if (h) setHighlightedStory(parseInt(h));
    }, []);

    const handleRun = async (storyId?: number) => {
        try {
            // Run button on story card
            if (storyId) {
                setSubmitError(undefined);
                setSingleStorySubmittingId(storyId);
                const res = await trigger({ storyIds: [storyId] });
                await refreshExecutions();
                navigate(
                    `/suite/${projectSlug}/${suiteSlug}/executions?executionId=${res?.execution.id}&run=${res?.runMessages[0]?.runId}_${res?.execution.id}&stepViewer=true`,
                );
                setSingleStorySubmittingId(undefined);
            } else {
                // let the back decides which stories to run
                setSubmitError(undefined);
                setIsSubmitting(true);
                const rawRun = await trigger({ storyIds: selectedStoryCards });
                const run = executionSchema.parse(rawRun);
                toast({
                    title: `Run ${run.execution.id} created`,
                    description: `Running ${selectedStoryCards.length > 0 ? selectedStoryCards.length : 'all active'} stories`,
                });
                setIsSubmitting(false);
                navigate(`/suite/${projectSlug}/${suiteSlug}/executions`);
            }
        } catch (err) {
            setIsSubmitting(false);
            setSingleStorySubmittingId(undefined);
            setSubmitError(err);
            toast({
                title: `Error starting run`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const { trigger: trigerArchive } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id/archive`, {
        revalidate: true,
        method: 'PATCH',
    });

    const handleArchive = async (storyId: number, storyName: string) => {
        setHighlightedStory(undefined);
        try {
            setSubmitError(undefined);
            await trigerArchive({ idParam: storyId });
            toast({
                title: `Archived story ${storyName}`,
            });
            mutate();
        } catch (err) {
            setSubmitError(err);
            toast({
                title: `Error archiving story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const { trigger: triggerEditMeta } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id`, {
        revalidate: true,
        method: 'PATCH',
    });

    const handleToggleEnable = async (storyId: number, enabled: boolean) => {
        setHighlightedStory(undefined);
        try {
            setSubmitError(undefined);
            await triggerEditMeta({ idParam: storyId, enabled });
            // toast({
            //     title: `Archived story ${storyId}`,
            // });
            mutate();
        } catch (err) {
            setSubmitError(err);
            toast({
                title: `Error ${enabled ? 'enabling' : 'disabling'} story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    return (
        <>
            <div className="flex flex-col min-h-full gap-y-2 overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)] px-4 pt-2">
                <div className="flex flex-row items-center justify-between border-b mb-8">
                    <div className="p-2 flex items-center gap-x-2 text-h4 w-full">
                        {block ? <BlocksIcon size={28} /> : <ListIcon size={28} />}
                        <span className="text-slate-500 flex items-center gap-x-2">
                            {block ? <div>Blocks in suite</div> : <div>Stories in suite</div>}
                            <span
                                className="font-extralight"
                                role="button"
                                title="Copy project/suite slug to clipboard"
                                onClick={() => {
                                    toast({
                                        title: 'Copied project/suite slug to clipboard',
                                        duration: 5000,
                                    });
                                    navigator.clipboard.writeText(`${projectSlug}/${suiteSlug}`);
                                }}
                            >
                                {projectSlug}/{suiteSlug}
                            </span>
                        </span>
                    </div>
                    <div role="toolbar" className="flex justify-between">
                        {block ? (
                            <>
                                {canOperate && (
                                    <Button className="mx-auto whitespace-nowrap ml-6" variant="default" asChild>
                                        <Link to={`/story/${projectSlug}/${suiteSlug}/live?block`}>Create block</Link>
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    className="ml-6 whitespace-nowrap mr-3"
                                    variant="secondary"
                                    onClick={() => setIsCiModalOpen(true)}
                                >
                                    Add to CI
                                </Button>
                                <Button
                                    className="mx-auto whitespace-nowrap w-24 mr-3"
                                    variant="secondary"
                                    onClick={() => handleRun()}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? '...'
                                        : selectedStoryCards.length === 0
                                          ? 'Run all'
                                          : `Run ${selectedStoryCards.length} selected`}
                                </Button>
                                {canOperate && (
                                    <div className="flex flex-col items-center">
                                        <Button className="mx-auto whitespace-nowrap " asChild>
                                            <Link to={`/story/${projectSlug}/${suiteSlug}/live`}>Create story</Link>
                                        </Button>
                                        {hasSuperUserPermissions && (
                                            <Link
                                                className="place-self-center items-center mx-auto text-xs underline"
                                                to={`/admin/story/${projectSlug}/${suiteSlug}/create`}
                                            >
                                                create story
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="sm:w-10/12 md:w-8/12 overflow-auto mx-auto">
                    {block && (
                        <p className="flex mb-6 text-sm mt-1">
                            A block is a group of steps that can be reused across stories (
                            <Link
                                target="_blank"
                                to="https://heal.dev/docs/how-to/write-tests#blocks"
                                className="underline text-muted-foreground flex flex-row items-center"
                            >
                                docs
                                <ExternalLinkIcon size={8} className="ml-1" />
                            </Link>
                            )
                        </p>
                    )}
                    {submitError && <DisplayError error={submitError} />}
                    <div>
                        {/* --- Story status counts --- */}
                        <StoriesSummary
                            numStoriesOn={numStoriesOn}
                            numStoriesMaintenance={numStoriesMaintenance}
                            numStoriesDraft={numStoriesDraft}
                            numStoriesDisabled={numStoriesDisabled}
                        />
                        <SearchBar className="flex-1" onChange={handleSearch} />
                    </div>

                    {isStoriesLoading && !storiesData ? (
                        <div className="grid grid-cols-1 gap-2 mt-6 overflow-x-hidden">
                            <StoryCard isLoading projectSlug={projectSlug ?? ''} suiteSlug={suiteSlug ?? ''} />
                            <StoryCard isLoading projectSlug={projectSlug ?? ''} suiteSlug={suiteSlug ?? ''} />
                            <StoryCard isLoading projectSlug={projectSlug ?? ''} suiteSlug={suiteSlug ?? ''} />
                        </div>
                    ) : errorStories ? (
                        <DisplayError error={errorStories} />
                    ) : nonArchivedStories.length === 0 ? (
                        <div className="flex flex-col items-center mt-12 gap-6">
                            <p className="text-center text-slate-800 w-full text-lg">
                                No {block ? 'block' : 'story'} yet
                            </p>
                            {canOperate && (
                                <>
                                    <NewButton className="mt-3" size={'large'} color={'purple'} asLink>
                                        <Link to={`/story/${projectSlug}/${suiteSlug}/live${block ? '?block' : ''}`}>
                                            Create {block ? 'block' : 'story'}
                                        </Link>
                                    </NewButton>
                                    <p className="text-center text-slate-800 w-full">
                                        Want to know more about {block ? 'blocks' : 'writing stories'}? Check out the{' '}
                                        <Link
                                            to={
                                                block
                                                    ? 'https://heal.dev/docs/how-to/reuse-steps-with-blocks'
                                                    : 'https://heal.dev/docs/how-to/write-tests'
                                            }
                                            target="_blank"
                                            className="underline text-purple-500"
                                        >
                                            docs.
                                        </Link>
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        projectSlug &&
                        suiteSlug && (
                            <div className="grid grid-cols-1 gap-2 mt-6 overflow-x-hidden">
                                {filteredData
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((story) => (
                                        <StoryCard
                                            story={story}
                                            isLoading={isStoriesLoading}
                                            key={story.id}
                                            handleRun={handleRun}
                                            projectSlug={projectSlug}
                                            suiteSlug={suiteSlug}
                                            isSubmitting={story.id === singleStorySubmittingId}
                                            selectedStoryCard={selectedStoryCards.includes(story.id)}
                                            setSelectedStoryCard={(selected) => {
                                                if (selected) {
                                                    if (!selectedStoryCards.includes(story.id))
                                                        setSelectedStoryCard([...selectedStoryCards, story.id]);
                                                } else {
                                                    setSelectedStoryCard(
                                                        selectedStoryCards.filter((id) => id !== story.id),
                                                    );
                                                }
                                            }}
                                            handleArchive={() => handleArchive(story.id, story.name)}
                                            handleToggleEnable={() => handleToggleEnable(story.id, !story.enabled)}
                                            fromStoryPage={true}
                                            stories={filteredData}
                                        />
                                    ))}
                            </div>
                        )
                    )}
                </div>
            </div>
            {isCiModalOpen && projectSlug && suiteSlug && (
                <AddToCiModal
                    projectSlug={projectSlug}
                    suiteSlug={suiteSlug}
                    isCiModalOpen={isCiModalOpen}
                    setIsCiModalOpen={setIsCiModalOpen}
                />
            )}
        </>
    );
};

export default SuiteDashboard;
